/**
 * PLAY TRIGGERS (STATES_AND_INTERACTIVE_ELEMENTS_PLAN §12 E, §14).
 *
 * An Appear effect, a count-up or a letters reveal can play because something
 * happens — this element or the one it follows is hovered, pressed, focused,
 * becomes the current page, is switched on, opens, is disabled, or enters a
 * custom state — instead of when it scrolls into view. Framework-free and
 * mirrored verbatim into the storefronts (scripts/sync-live-host.mjs).
 *
 * Stored on the element (all `style_` keys, so undo and component copies
 * treat them like every other style value):
 *   style_animation_trigger  'event'   (beside '', 'load', 'hover')
 *   style_animation_event    hover | pressed | focus | current | on | open | disabled | state:<name>
 *   style_animation_follow   '' (this element) | 'card' | an ancestor's id
 *   style_animation_reverse  '' (the event's default) | 'yes' (play back) | 'no' (back to the start)
 *                            | 'stay' (plays once, then keeps its end look — a count that rolls up
 *                              on the first hover and stays)
 *
 * The element carries `data-pb-play-on` / `-source` / `-reverse`; the bake
 * holds its animation paused at the start under `.pb-play-ready` (set by the
 * page runtime, never under reduced motion); the runtime plays it forward when
 * the event begins and back — or straight back to the start — when it ends.
 * Without JavaScript the element simply shows at rest.
 */

export const EVENT_TRIGGER = 'event';

export const PLAY_EVENTS = ['hover', 'pressed', 'focus', 'current', 'on', 'open', 'disabled'] as const;
export type BuiltInPlayEvent = (typeof PLAY_EVENTS)[number];

/** Pointer and keyboard moments undo themselves on leave; a state plays once (John, 2026-10-12). */
export function defaultReverse(event: string): boolean {
  return event === 'hover' || event === 'pressed' || event === 'focus';
}

/** What happens when the event ends. */
export type PlayEnd = 'reverse' | 'reset' | 'stay';

export interface PlayTrigger {
  event: string;
  /** '' = this element; 'card' = the card around it; otherwise an ancestor's id. */
  source: string;
  end: PlayEnd;
}

/** An instance child's follow is stored relative to its definition (state-styles resolveFollow). */
function resolveSource(ownId: string, stored: string): string {
  if (!stored || stored === 'card') return stored;
  const at = ownId.lastIndexOf('__');
  if (at < 0) return stored;
  const prefix = ownId.slice(0, at + 2);
  return stored.startsWith(prefix) ? stored : `${prefix}${stored}`;
}

const EVENT_PATTERN = /^(hover|pressed|focus|current|on|open|disabled|state:[a-z][a-z0-9-]{0,31})$/;

export function readPlayTrigger(props: Record<string, unknown>, ownId = ''): PlayTrigger | null {
  if (String(props.style_animation_trigger ?? '') !== EVENT_TRIGGER) return null;
  const event = String(props.style_animation_event || 'hover');
  if (!EVENT_PATTERN.test(event)) return null;
  const setting = String(props.style_animation_reverse ?? '');
  const end: PlayEnd = setting === 'yes' ? 'reverse' : setting === 'no' ? 'reset' : setting === 'stay' ? 'stay'
    : defaultReverse(event) ? 'reverse' : 'reset';
  return { event, source: resolveSource(ownId, String(props.style_animation_follow ?? '')), end };
}

/** The attributes an element with a play trigger carries (both render paths, every renderer). */
export function playAttrs(props: Record<string, unknown>, ownId = ''): Record<string, string> {
  const t = readPlayTrigger(props, ownId);
  if (!t) return {};
  return {
    'data-pb-play-on': t.event,
    ...(t.source ? { 'data-pb-play-source': t.source } : {}),
    // yes = play back, no = back to the start, stay = once, then keep the end.
    'data-pb-play-reverse': t.end === 'reverse' ? 'yes' : t.end === 'stay' ? 'stay' : 'no',
  };
}

/** Is the event's state on, read from the source element's attributes? */
export function stateIsOn(event: string, source: Element): boolean {
  switch (event) {
    case 'current':
      return ['page', 'true'].includes(source.getAttribute('aria-current') ?? '');
    case 'on':
      return source.getAttribute('aria-pressed') === 'true';
    case 'open':
      return source.getAttribute('aria-expanded') === 'true';
    case 'disabled':
      return source.getAttribute('aria-disabled') === 'true';
    default:
      if (event.startsWith('state:')) {
        return (source.getAttribute('data-pb-state') ?? '').split(/\s+/).includes(event.slice(6));
      }
      return false;
  }
}

/** The element whose event drives `el`. Null when that ancestor is not on the page. */
export function playSource(el: Element): Element | null {
  const source = el.getAttribute('data-pb-play-source') ?? '';
  if (!source) return el;
  if (source === 'card') return el.closest('.pb-card-link-boundary');
  const safe = source.replace(/[^A-Za-z0-9_-]/g, '');
  return el.closest(`.pb-${safe}`) ?? el.closest(`[data-bp-id="${safe}"]`);
}

// ── Count-up, played by a trigger ─────────────────────────────────────────────

export interface CountParts {
  prefix: string;
  suffix: string;
  decimals: number;
  from: number;
  to: number;
}

/** "From $1,200+" style text around the number; decimals follow `to`. */
export function countParts(el: Element): CountParts | null {
  const to = Number(el.getAttribute('data-pb-count-to'));
  if (!Number.isFinite(to)) return null;
  const from = Number(el.getAttribute('data-pb-count-from') ?? '0') || 0;
  const text = el.textContent ?? '';
  const m = /^(\D*)(-?[\d.,]+)(.*)$/s.exec(text.trim());
  const decimals = (String(el.getAttribute('data-pb-count-to')).split('.')[1] ?? '').length;
  return { prefix: m ? m[1] : '', suffix: m ? m[3] : '', decimals, from, to };
}

export function formatCount(value: number, parts: CountParts): string {
  // toFixed, as the scroll count-up does (runtime-scripts pbCount), so the two agree.
  return `${parts.prefix}${value.toFixed(parts.decimals)}${parts.suffix}`;
}
